import { deleteField, doc, updateDoc } from 'firebase/firestore'
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
import { useCallback, useState } from 'react'
import { newId, uploadContentType } from '@sonder/shared'
import { useAuth } from '@/auth/AuthProvider'
import { db, storage } from '@/lib/firebase'
import { toFieldPath } from './hooks'
import type { UploadJob } from '@/ui'

const MAX_BYTES = 100 * 1024 * 1024
const ALLOWED = /^(image\/|video\/|application\/pdf$|application\/zip$|application\/postscript$|font\/)/

/** Uploads to clients/{c}/uploads/{fileId}/{name} and records metadata in the assets section doc. */
export function useUploads(clientId: string, extra: { brandId?: string | null; assetKey?: string | null } = {}) {
  const { user } = useAuth()
  const [jobs, setJobs] = useState<UploadJob[]>([])
  const patch = (id: string, p: Partial<UploadJob>) => setJobs((js) => js.map((j) => (j.id === id ? { ...j, ...p } : j)))

  const upload = useCallback(
    (files: File[]) => {
      for (const file of files) {
        const id = newId()
        setJobs((js) => [...js, { id, name: file.name, progress: 0 }])
        if (file.size > MAX_BYTES) {
          patch(id, { error: 'Over 100 MB' })
          continue
        }
        const contentType = uploadContentType(file.name, file.type)
        if (!ALLOWED.test(contentType)) {
          patch(id, { error: 'File type not accepted' })
          continue
        }
        const safeName = file.name.replace(/[^\w.\- ]+/g, '_')
        const storagePath = `clients/${clientId}/uploads/${id}/${safeName}`
        const task = uploadBytesResumable(ref(storage, storagePath), file, { contentType })
        task.on(
          'state_changed',
          (s) => patch(id, { progress: (s.bytesTransferred / s.totalBytes) * 100 }),
          (e) => patch(id, { error: e.code === 'storage/unauthorized' ? 'Not allowed' : 'Upload failed' }),
          async () => {
            await updateDoc(doc(db, `clients/${clientId}/sections/assets`), toFieldPath(`uploads.${id}`), {
              name: file.name, storagePath, size: file.size, contentType,
              brandId: extra.brandId ?? null, assetKey: extra.assetKey ?? null, category: null, note: null, uploadedBy: user?.uid ?? '', at: Date.now(),
            })
            patch(id, { progress: 100 })
            window.setTimeout(() => setJobs((js) => js.filter((j) => j.id !== id)), 1500)
          },
        )
      }
    },
    [clientId, extra.brandId, extra.assetKey, user?.uid],
  )

  const remove = useCallback(
    async (fileId: string, storagePath: string) => {
      await deleteObject(ref(storage, storagePath)).catch(() => undefined)
      await updateDoc(doc(db, `clients/${clientId}/sections/assets`), toFieldPath(`uploads.${fileId}`), deleteField())
    },
    [clientId],
  )

  return { jobs, upload, remove }
}

export async function downloadUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath))
}
